import { useEffect, useMemo, useState, Fragment } from 'react';
import {
  CalendarRange,
  MapPin,
  Package2,
  Receipt,
  TrendingUp,
  CheckCheck,
  AlertTriangle,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { Input } from '@/components/ui/Input';
import { ExportStockButton } from '@/components/events/ExportStockButton';
import {
  useCloseEvent,
  useEventSummary,
} from '@/hooks/useEvents';
import {
  useEventAllocations,
} from '@/hooks/useEventAllocations';
import { useActiveEventStore } from '@/hooks/useActiveEvent';
import { useAuth } from '@/hooks/useAuth';
import { formatDate, formatDateTime, formatIDR, formatNumber } from '@/lib/format';
import { groupAllocationsByProduct } from '@/lib/allocationGroups';
import type { EventStockAllocation } from '@/types/domain';

export interface CloseEventDialogProps {
  eventId: string | null;
  open: boolean;
  onClose: () => void;
}

export function CloseEventDialog({ eventId, open, onClose }: CloseEventDialogProps) {
  const closeEvent = useCloseEvent();
  const clearActive = useActiveEventStore((s) => s.clear);
  const activeEventId = useActiveEventStore((s) => s.activeEventId);
  const { user } = useAuth();
  const { data: summary, isLoading: loadingSummary } = useEventSummary(eventId);
  const { data: allocations, isLoading: loadingAllocs } = useEventAllocations(eventId);

  // Keyed by allocation id, not product: a product can hold several lots in
  // one event and each comes back to the warehouse on its own count. Keying by
  // product would write one lot's figure over another's.
  const [returnByAllocation, setReturnByAllocation] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Reset the form when the dialog opens for a new event
  useEffect(() => {
    if (open && allocations) {
      const defaults: Record<string, string> = {};
      for (const a of allocations) {
        const remaining = a.qtyIn - a.qtySold;
        defaults[a.id] = String(Math.max(0, remaining));
      }
      setReturnByAllocation(defaults);
      setError(null);
    }
    if (!open) {
      setReturnByAllocation({});
      setError(null);
    }
  }, [open, allocations]);

  const handleMarkAllReturned = () => {
    if (!allocations) return;
    const next: Record<string, string> = {};
    for (const a of allocations) {
      const remaining = a.qtyIn - a.qtySold;
      next[a.id] = String(Math.max(0, remaining));
    }
    setReturnByAllocation(next);
  };

  const totals = useMemo(() => {
    let totalIn = 0;
    let totalSold = 0;
    let totalReturned = 0;
    let totalLoss = 0;
    for (const a of allocations ?? []) {
      totalIn += a.qtyIn;
      totalSold += a.qtySold;
      const remaining = a.qtyIn - a.qtySold;
      const returned = parseInt(returnByAllocation[a.id] ?? '0', 10) || 0;
      totalReturned += returned;
      totalLoss += Math.max(0, remaining - returned);
    }
    return { totalIn, totalSold, totalReturned, totalLoss };
  }, [allocations, returnByAllocation]);

  const handleClose = async () => {
    if (!eventId || !user) return;
    setError(null);

    // Build returns map; only include products with allocations.
    const returns: Record<string, number> = {};
    for (const a of allocations ?? []) {
      const v = parseInt(returnByAllocation[a.id] ?? '0', 10) || 0;
      returns[a.id] = v;
    }

    // The number field's `max` only constrains its spinner — a typed value
    // sails past it. close_event() rejects an over-count now, but catching it
    // here names the product instead of surfacing a raw RPC error.
    const overCount = (allocations ?? []).find((a) => {
      const remaining = Math.max(0, a.qtyIn - a.qtySold);
      return (returns[a.id] ?? 0) > remaining;
    });
    if (overCount) {
      setError(
        `Qty kembali gudang untuk ${overCount.productName}` +
          `${overCount.lotNumber ? ` (lot ${overCount.lotNumber})` : ''} melebihi sisa di event ` +
          `(${Math.max(0, overCount.qtyIn - overCount.qtySold)} unit).`,
      );
      return;
    }

    try {
      // One call: close_event() records the returns and closes the event in the
      // same transaction. Calling it twice fails the second time.
      await closeEvent.mutateAsync({ id: eventId, returns });
      if (activeEventId === eventId) clearActive();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const isLoading = loadingSummary || loadingAllocs;
  const hasAllocations = (allocations?.length ?? 0) > 0;
  const noReturnProducts = hasAllocations && totals.totalLoss === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Close event?"
      description={
        summary
          ? `Tutup "${summary.event.name}" — penjualan akan di-freeze.`
          : 'Tutup event ini.'
      }
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={closeEvent.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleClose}
            loading={closeEvent.isPending}
            disabled={isLoading}
          >
            Close event
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
        </div>
      )}

      {isLoading || !summary ? (
        <div className="py-12 text-center text-ink-subtle text-small">Loading summary…</div>
      ) : (
        <div className="space-y-5">
          <div className="p-4 rounded-lg bg-canvas border border-line space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-subhead font-display font-semibold text-ink truncate">
                  {summary.event.name}
                </p>
                <code className="text-caption font-mono text-ink-subtle">
                  {summary.event.code}
                </code>
              </div>
              <Chip variant="active">Active</Chip>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-caption text-ink-subtle">
              <span className="inline-flex items-center gap-1">
                <CalendarRange size={12} />
                {formatDateTime(summary.event.startAt)}
              </span>
              {summary.event.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={12} />
                  {summary.event.location}
                </span>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <SummaryStat
              icon={<Receipt size={16} />}
              label="Total transaksi"
              value={formatNumber(summary.totalTransactions)}
              hint={
                summary.cancelledCount > 0
                  ? `${formatNumber(summary.cancelledCount)} cancelled di-exclude`
                  : 'Semua status non-cancelled'
              }
            />
            <SummaryStat
              icon={<TrendingUp size={16} />}
              label="Total penjualan"
              value={formatIDR(summary.totalSales)}
              hint="Grand total (setelah diskon + PPN)"
            />
          </div>

          {/* Stock reconciliation */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-small font-medium text-ink">Stock reconciliation</p>
              {hasAllocations && (
                <div className="flex items-center gap-1">
                  <ExportStockButton
                    event={summary.event}
                    allocations={allocations ?? []}
                    size="sm"
                    variant="ghost"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<CheckCheck size={14} />}
                    onClick={handleMarkAllReturned}
                  >
                    Tandai semua kembali
                  </Button>
                </div>
              )}
            </div>

            {hasAllocations ? (
              <div className="space-y-2">
                <Table>
                  <THead>
                    <TR>
                      <TH>Produk</TH>
                      <TH className="text-right">Qty in</TH>
                      <TH className="text-right">Sold</TH>
                      <TH className="text-right">Remaining</TH>
                      <TH className="text-right">Kembali</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {groupAllocationsByProduct(allocations ?? []).map((g) => (
                      <Fragment key={g.productId}>
                        {/* The product once; each lot gets its own count below
                            it, because each lot is counted and returned on its
                            own. */}
                        <TR className="bg-canvas/60">
                          <TD>
                            <p className="font-medium text-ink">{g.productName}</p>
                            <p className="text-caption text-ink-subtle">
                              <code className="font-mono">{g.productSku}</code> · {g.lots.length} lot
                            </p>
                          </TD>
                          <TD className="text-right tabular-nums font-medium">{formatNumber(g.qtyIn)}</TD>
                          <TD className="text-right tabular-nums font-medium">{formatNumber(g.qtySold)}</TD>
                          <TD className="text-right tabular-nums font-medium">{formatNumber(g.available)}</TD>
                          <TD className="text-right tabular-nums font-medium">
                            {formatNumber(
                              g.lots.reduce((n, l) => n + (parseInt(returnByAllocation[l.id] ?? '', 10) || 0), 0),
                            )}
                          </TD>
                        </TR>
                        {g.lots.map((a) => (
                          // Keyed by allocation id — NOT productId. One product can
                          // sit in several lots; keying the inputs by product made
                          // every lot share a single box, and because the totals and
                          // payload read the allocation key, whatever was typed here
                          // never reached close_event() at all.
                          <AllocationRow
                            key={a.id}
                            alloc={a}
                            value={returnByAllocation[a.id] ?? ''}
                            onChange={(v) =>
                              setReturnByAllocation((m) => ({ ...m, [a.id]: v }))
                            }
                          />
                        ))}
                      </Fragment>
                    ))}
                  </TBody>
                </Table>

                <div className="grid grid-cols-4 gap-2 text-caption text-center">
                  <ReconcileStat label="Total qty in" value={formatNumber(totals.totalIn)} />
                  <ReconcileStat label="Sold" value={formatNumber(totals.totalSold)} />
                  <ReconcileStat label="Kembali" value={formatNumber(totals.totalReturned)} />
                  <ReconcileStat
                    label="Loss"
                    value={formatNumber(totals.totalLoss)}
                    tone={totals.totalLoss === 0 ? 'success' : 'danger'}
                  />
                </div>

                {noReturnProducts ? (
                  <div className="p-3 rounded border border-success/30 bg-success/5 text-caption text-success inline-flex items-center gap-2">
                    <CheckCheck size={14} />
                    Semua produk kembali ke gudang — tidak ada loss.
                  </div>
                ) : (
                  <div className="p-3 rounded border border-warning/30 bg-warning/5 text-caption text-warning inline-flex items-center gap-2">
                    <AlertTriangle size={14} />
                    Ada loss total {formatNumber(totals.totalLoss)} unit. Pastikan ini sudah benar.
                  </div>
                )}
              </div>
            ) : (
              <div className="py-6 text-center text-ink-subtle text-small border border-dashed border-line rounded">
                Event ini tidak punya alokasi stok. Tutup langsung?
              </div>
            )}
          </div>

          {summary.topProducts.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-small font-medium text-ink">Top 5 produk</p>
                <p className="text-caption text-ink-subtle">Diurutkan per qty</p>
              </div>
              <Table>
                <THead>
                  <TR>
                    <TH>Produk</TH>
                    <TH className="text-right">Qty</TH>
                    <TH className="text-right">Subtotal</TH>
                  </TR>
                </THead>
                <TBody>
                  {summary.topProducts.map((p) => (
                    <TR key={p.productId}>
                      <TD>
                        <div className="flex items-center gap-2">
                          <Package2 size={14} className="text-ink-subtle" />
                          <span className="text-body text-ink truncate max-w-xs">
                            {p.name}
                          </span>
                        </div>
                      </TD>
                      <TD className="text-right font-medium">{formatNumber(p.qty)}</TD>
                      <TD className="text-right font-medium">{formatIDR(p.subtotal)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function AllocationRow({
  alloc,
  value,
  onChange,
}: {
  alloc: EventStockAllocation;
  value: string;
  onChange: (v: string) => void;
}) {
  const remaining = alloc.qtyIn - alloc.qtySold;
  const returned = parseInt(value, 10) || 0;
  const loss = Math.max(0, remaining - returned);
  return (
    <TR>
      <TD className="!pl-8">
        <p className="text-small text-ink flex flex-wrap gap-x-3">
          <span>
            <span className="text-ink-subtle">Lot </span>
            <span className="font-mono">{alloc.lotNumber || '—'}</span>
          </span>
          <span className="text-ink-subtle">ED {alloc.expiryDate ? formatDate(alloc.expiryDate) : '—'}</span>
        </p>
      </TD>
      <TD className="text-right tabular-nums">{formatNumber(alloc.qtyIn)}</TD>
      <TD className="text-right tabular-nums">{formatNumber(alloc.qtySold)}</TD>
      <TD className="text-right tabular-nums font-medium">
        {formatNumber(Math.max(0, remaining))}
      </TD>
      <TD>
        <div className="flex justify-end items-center gap-2">
          <Input
            type="number"
            min={0}
            max={remaining}
            step={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-20 text-right"
          />
          {loss > 0 && (
            <span className="text-caption text-danger tabular-nums">-{formatNumber(loss)}</span>
          )}
        </div>
      </TD>
    </TR>
  );
}

function SummaryStat({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="p-4 rounded-lg bg-canvas border border-line">
      <div className="flex items-center gap-2 text-overline uppercase text-ink-subtle font-medium">
        {icon}
        {label}
      </div>
      <p className="text-metric font-display font-semibold text-ink mt-2">{value}</p>
      {hint && <p className="text-caption text-ink-subtle mt-1">{hint}</p>}
    </div>
  );
}

function ReconcileStat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'danger';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success border-success/30 bg-success/5'
      : tone === 'danger'
        ? 'text-danger border-danger/30 bg-danger/5'
        : 'text-ink border-line bg-canvas';
  return (
    <div className={`p-2 rounded border ${toneClass}`}>
      <p className="text-overline uppercase opacity-80">{label}</p>
      <p className="text-body font-display font-semibold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}
